import { supabase } from '../lib/supabase';

export interface KarelPagingRequest {
  orderBy?: {
    accessor: string;
    columnName: string;
    direction: 'ASC' | 'DESC';
  }[];
  page: number;
  rowSize: number;
}

export interface KarelMissedCallRequest {
  diagramName?: string;
  endDateTime: string;
  onlyNotCallback?: boolean;
  paging?: KarelPagingRequest;
  pressKeys?: boolean;
  ringAgent?: boolean;
  startDateTime: string;
}

export interface KarelMissedCallData {
  assigmentDate: string | null;
  assignmentInternalId: number;
  callId: string;
  callPhoneNumber: string;
  callStartDate: string;
  diagramName: string;
  fullCount: number;
  hangupId: number;
  missedCallReturnStatus: string | null;
  pressKeys: boolean;
  returnCallStartTime: string | null;
  returnPhoneNumber: string | null;
  ringAgent: boolean;
  trunkId: string;
}

export interface KarelMissedCallResponse {
  data: KarelMissedCallData[];
  message: string;
  params: Record<string, string>;
  resultId: number;
}

export interface MissedCallSyncResult {
  success: boolean;
  newCalls: number;
  updatedCalls: number;
  errors: string[];
  totalProcessed: number;
  userResults: Array<{
    userId: string;
    userName: string;
    success: boolean;
    newCalls: number;
    error?: string;
  }>;
}

export class KarelMultiUserSyncService {
  private syncInProgress = false;
  private lastSyncTime: Date | null = null;
  private karelApiBaseUrl: string;

  constructor() {
    this.karelApiBaseUrl = import.meta.env.VITE_KAREL_API_BASE_URL || 'https://connect.karel.cloud';
  }

  async fetchMissedCallsForUser(
    userId: string,
    userToken: string,
    startDate: Date,
    endDate: Date,
    onlyNotCallback: boolean = true,
    options?: {
      pressKeys?: boolean;
      ringAgent?: boolean;
      diagramName?: string;
    }
  ): Promise<KarelMissedCallResponse> {
    const allData: KarelMissedCallData[] = [];
    let currentPage = 1;
    const rowSize = 100;
    let hasMore = true;
    let lastResponse: KarelMissedCallResponse | null = null;
    let loopCount = 0;
    const MAX_LOOPS = 100; // Safety break (100 * 100 = 10,000 calls max per sync)

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // Increased timeout for bulk fetch

    try {
      while (hasMore && loopCount < MAX_LOOPS) {
        loopCount++;

        const request: KarelMissedCallRequest = {
          startDateTime: startDate.toISOString(),
          endDateTime: endDate.toISOString(),
          onlyNotCallback,
          pressKeys: options?.pressKeys,
          ringAgent: options?.ringAgent,
          diagramName: options?.diagramName,
          paging: {
            orderBy: [
              {
                accessor: 'callStartDate',
                columnName: 'callStartDate',
                direction: 'DESC',
              },
            ],
            page: currentPage,
            rowSize: rowSize,
          },
        };

        const response = await fetch(`${this.karelApiBaseUrl}/token/reports/getmissedcallreport2`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'TokenId': userToken,
          },
          body: JSON.stringify(request),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Karel API error: ${response.status}`);
        }

        const jsonResponse: KarelMissedCallResponse = await response.json();
        lastResponse = jsonResponse;

        if (jsonResponse.data && Array.isArray(jsonResponse.data)) {
          allData.push(...jsonResponse.data);

          if (jsonResponse.data.length < rowSize) {
            hasMore = false;
          } else {
            currentPage++;
          }
        } else {
          hasMore = false;
        }
      }

      clearTimeout(timeoutId);

      // Return combined response
      return {
        data: allData,
        message: lastResponse?.message || 'Success',
        params: lastResponse?.params || {},
        resultId: lastResponse?.resultId || 0
      };

    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Karel API timeout (60s)');
      }
      throw error;
    }
  }

  async syncAllUsersMissedCalls(
    startDate?: Date,
    endDate?: Date,
    onlyNotCallback: boolean = true,
    options?: {
      pressKeys?: boolean;
      ringAgent?: boolean;
      diagramName?: string;
    }
  ): Promise<MissedCallSyncResult> {
    if (this.syncInProgress) {
      return {
        success: false,
        newCalls: 0,
        updatedCalls: 0,
        errors: ['Senkronizasyon zaten devam ediyor'],
        totalProcessed: 0,
        userResults: [],
      };
    }

    this.syncInProgress = true;
    const result: MissedCallSyncResult = {
      success: true,
      newCalls: 0,
      updatedCalls: 0,
      errors: [],
      totalProcessed: 0,
      userResults: [],
    };

    try {
      const end = endDate || new Date();
      const start = startDate || new Date(end.getTime() - 24 * 60 * 60 * 1000);

      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('id, name, email, karel_token')
        .eq('is_active', true)
        .not('karel_token', 'is', null);

      if (usersError) throw usersError;

      if (!users || users.length === 0) {
        result.errors.push('Karel token\'ı olan aktif kullanıcı bulunamadı');
        result.success = false;
        this.syncInProgress = false;
        return result;
      }

      console.log(`[Karel Multi-User Sync] ${users.length} kullanıcı için paralel senkronizasyon başlatılıyor...`);

      const userProcessingPromises = users.map(async (user) => {
        const userResult: {
          userId: string;
          userName: string;
          success: boolean;
          newCalls: number;
          error?: string;
        } = {
          userId: user.id,
          userName: user.name,
          success: false,
          newCalls: 0,
        };

        try {
          const karelResponse = await this.fetchMissedCallsForUser(
            user.id,
            user.karel_token,
            start,
            end,
            onlyNotCallback,
            options
          );

          console.log(`[${user.name}] ${karelResponse.data?.length || 0} çağrı alındı`);

          if (karelResponse.data && karelResponse.data.length > 0) {
            const processed = await this.processMissedCallsBatch(karelResponse.data, user.id);
            userResult.newCalls = processed.newCalls;
            result.newCalls += processed.newCalls;
            result.totalProcessed += processed.totalProcessed;
          }

          userResult.success = true;
          return userResult;

        } catch (error: any) {
          console.error(`[${user.name}] Hata:`, error.message);
          userResult.success = false;
          userResult.error = error.message;
          result.errors.push(`${user.name}: ${error.message}`);
          return userResult;
        }
      });

      result.userResults = await Promise.all(userProcessingPromises);

      this.lastSyncTime = new Date();

    } catch (error: any) {
      result.success = false;
      result.errors.push(`Genel senkronizasyon hatası: ${error.message}`);
    } finally {
      this.syncInProgress = false;
    }

    return result;
  }

  private async processMissedCallsBatch(
    karelCalls: KarelMissedCallData[],
    assignedUserId: string
  ): Promise<{ newCalls: number; totalProcessed: number }> {
    if (!karelCalls || karelCalls.length === 0) {
      return { newCalls: 0, totalProcessed: 0 };
    }

    const { data: userProfile } = await supabase
      .from('profiles')
      .select('branch_id')
      .eq('id', assignedUserId)
      .single();

    if (!userProfile?.branch_id) {
      console.error('[Batch Insert] Kullanıcının branch_id bilgisi bulunamadı');
      return { newCalls: 0, totalProcessed: 0 };
    }

    // Tüm şubeleri çekelim (Diagram eşleştirmesi için)
    const { data: allBranches } = await supabase
      .from('branches')
      .select('id, name');

    const branchMap = new Map<string, string>();
    if (allBranches) {
      allBranches.forEach(b => {
        // Ham ismi ekle
        branchMap.set(b.name.toLowerCase(), b.id);

        // "Şubesi", "Branch" gibi ekleri temizleyip "yalın" ismi ekle
        // Örn: "Kadıköy Şubesi" -> "kadikoy"
        let cleanName = b.name.toLowerCase()
          .replace(' şubesi', '')
          .replace(' subesi', '')
          .replace(' branch', '')
          .trim();

        branchMap.set(cleanName, b.id);

        // Türkçe karakter temizlenmiş versiyonunu da ekle
        const normalizedName = cleanName
          .replace(/ğ/g, 'g')
          .replace(/ü/g, 'u')
          .replace(/ş/g, 's')
          .replace(/ı/g, 'i')
          .replace(/ö/g, 'o')
          .replace(/ç/g, 'c');
        branchMap.set(normalizedName, b.id);
      });
    }

    const callIds = karelCalls.map(c => c.callId);

    const { data: existingCalls } = await supabase
      .from('missed_calls')
      .select('call_id')
      .in('call_id', callIds);

    const existingCallIds = new Set(existingCalls?.map(c => c.call_id) || []);
    const newCalls = karelCalls.filter(c => !existingCallIds.has(c.callId));

    if (newCalls.length === 0) {
      return { newCalls: 0, totalProcessed: karelCalls.length };
    }

    // Merkez şubesini bul
    let merkezBranchId: string | null = null;
    const merkezBranch = allBranches?.find(b => b.name.toLowerCase().includes('merkez'));
    if (merkezBranch) {
      merkezBranchId = merkezBranch.id;
    } else {
      console.warn('Merkez şube bulunamadı, varsayılan şube kullanılıyor.');
    }

    const normalizedPhones = newCalls.map(c => this.normalizePhoneNumber(c.callPhoneNumber));
    const { data: matchedLeads } = await supabase
      .from('leads')
      .select('id, phone')
      .in('phone', normalizedPhones);

    const phoneToLeadIdMap = new Map(matchedLeads?.map(l => [l.phone, l.id]) || []);

    const insertData = newCalls.map(karelCall => {
      const normalizedPhone = this.normalizePhoneNumber(karelCall.callPhoneNumber);

      // API'den gelen veride diagramName bazen DiagramName (PascalCase) olabilir
      // @ts-ignore
      const rawDiagramName = karelCall.diagramName || karelCall.DiagramName || '';

      // Diagram adından şube bulma
      // Varsayılan olarak: Merkez Şube (Eğer bulunamazsa sync yapan kullanıcının şubesi)
      let targetBranchId = merkezBranchId || userProfile.branch_id;
      let targetAssignedTo: string | null = null; // Varsayılan olarak kimseye atanmasın (havuza düşsün)

      if (rawDiagramName && branchMap.size > 0) {
        const diagramLower = rawDiagramName.toLowerCase();
        // Türkçe karakter temizle
        const diagramNormalized = diagramLower
          .replace(/ğ/g, 'g')
          .replace(/ü/g, 'u')
          .replace(/ş/g, 's')
          .replace(/ı/g, 'i')
          .replace(/ö/g, 'o')
          .replace(/ç/g, 'c');

        for (const [name, id] of branchMap.entries()) {
          // İki yönlü kontrol:
          // 1. Diagram adı şube adını içeriyor mu? (Örn: diagram="Kadıköy Satış" branch="kadıköy") -> EVET
          // 2. Şube adı diagram adını içeriyor mu? (Örn: diagram="kadıköy" branch="kadıköy şubesi" [temizlenmiş hali "kadıköy"]) -> EVET
          if (diagramNormalized.includes(name) || name.includes(diagramNormalized)) {
            targetBranchId = id;
            break;
          }
        }
      }

      return {
        call_id: karelCall.callId,
        call_phone_number: karelCall.callPhoneNumber,
        call_start_date: new Date(karelCall.callStartDate).toISOString(),
        diagram_name: rawDiagramName,
        hangup_id: karelCall.hangupId,
        missed_call_return_status: karelCall.missedCallReturnStatus,
        trunk_id: karelCall.trunkId,
        press_keys: karelCall.pressKeys,
        ring_agent: karelCall.ringAgent,
        assigned_to: targetAssignedTo,
        branch_id: targetBranchId,
        status: karelCall.returnCallStartTime ? 'called_back' : 'pending',
        lead_id: phoneToLeadIdMap.get(normalizedPhone) || null,
        synced_at: new Date().toISOString(),
      };
    });

    const { error } = await supabase.from('missed_calls').insert(insertData);

    if (error) {
      console.error('[Batch Insert] Hata:', error);
      return { newCalls: 0, totalProcessed: karelCalls.length };
    }

    return { newCalls: newCalls.length, totalProcessed: karelCalls.length };
  }

  private async processMissedCall(
    karelCall: KarelMissedCallData,
    assignedUserId: string
  ): Promise<boolean> {
    const { data: existingCall } = await supabase
      .from('missed_calls')
      .select('id')
      .eq('call_id', karelCall.callId)
      .maybeSingle();

    if (existingCall) {
      return false;
    }

    const normalizedPhone = this.normalizePhoneNumber(karelCall.callPhoneNumber);
    const { data: matchedLead } = await supabase
      .from('leads')
      .select('id')
      .eq('phone', normalizedPhone)
      .maybeSingle();

    const insertData = {
      call_id: karelCall.callId,
      call_phone_number: karelCall.callPhoneNumber,
      call_start_date: new Date(karelCall.callStartDate).toISOString(),
      diagram_name: karelCall.diagramName,
      hangup_id: karelCall.hangupId,
      missed_call_return_status: karelCall.missedCallReturnStatus,
      trunk_id: karelCall.trunkId,
      press_keys: karelCall.pressKeys,
      ring_agent: karelCall.ringAgent,
      assigned_to: assignedUserId,
      status: karelCall.returnCallStartTime ? 'called_back' : 'pending',
      lead_id: matchedLead?.id || null,
      synced_at: new Date().toISOString(),
    };

    const { error } = await supabase.from('missed_calls').insert(insertData);

    if (error) {
      return false;
    }

    return true;
  }

  normalizePhoneNumber(phoneNumber: string): string {
    let cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.startsWith('90')) {
      cleaned = cleaned.slice(2);
    } else if (cleaned.startsWith('0')) {
      cleaned = cleaned.slice(1);
    }

    if (cleaned.length === 10 && cleaned.startsWith('5')) {
      return cleaned;
    }

    return phoneNumber;
  }

  async syncLast24Hours(
    onlyNotCallback: boolean = true,
    options?: { pressKeys?: boolean; ringAgent?: boolean; diagramName?: string }
  ): Promise<MissedCallSyncResult> {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
    return this.syncAllUsersMissedCalls(startDate, endDate, onlyNotCallback, options);
  }

  async syncLast7Days(
    onlyNotCallback: boolean = true,
    options?: { pressKeys?: boolean; ringAgent?: boolean; diagramName?: string }
  ): Promise<MissedCallSyncResult> {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    return this.syncAllUsersMissedCalls(startDate, endDate, onlyNotCallback, options);
  }

  async syncLastMonth(
    onlyNotCallback: boolean = true,
    options?: { pressKeys?: boolean; ringAgent?: boolean; diagramName?: string }
  ): Promise<MissedCallSyncResult> {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
    return this.syncAllUsersMissedCalls(startDate, endDate, onlyNotCallback, options);
  }

  getLastSyncTime(): Date | null {
    return this.lastSyncTime;
  }

  isSyncInProgress(): boolean {
    return this.syncInProgress;
  }
}

export const karelMultiUserSyncService = new KarelMultiUserSyncService();
